
import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Translation, useTranslation } from 'react-i18next';
import { Tooltip, Spin } from 'antd';
import _style from './StatusItem.scss';
import HourGlassIcon from 'svg/v2/newcs_dashboard_list_status_icon1.svg';
import WaitingIcon from 'svg/v2/newcs_dashboard_list_status_icon2.svg';
import SuccessIcon from 'svg/v2/newcs_dashboard_list_status_icon3.svg';
import ReopenedIcon from 'svg/v2/newcs_dashboard_list_status_icon4.svg';
import { IssueStatus } from 'utils/constants/issue-status';
import RestHelper from 'utils/RestHelper';
import { useSessionStorageState } from 'ahooks';
import { ze } from 'utils/zhEn';
import {useConvertTimeToAppTimeZone} from "utils/react-hooks/crashsight-hooks";

const STATUS_IMG = {
  0: <HourGlassIcon />, // 未处理
  1: <SuccessIcon />, // 已处理
  2: <WaitingIcon />, // 处理中
  [IssueStatus.REOPENED]: <ReopenedIcon/>,
};

/**
 * Tooltip内容组件
 * 需求：在鼠标悬浮时，除了显示处理人列表，还需要显示问题最近一次状态修改的时间
 *
 * 实现：
 * 1. 当组件挂载时，调用接口获取状态变更历史
 * 2. 使用sessionStorage缓存接口结果，相同issueId一小时内不重复调用
 * 3. 显示三行内容：处理人、状态、时间
 *
 * @param {string} appId - 应用ID
 * @param {string|number} pid - 平台ID（platformId）
 * @param {string} issueId - 问题ID
 * @param {string[]} nicknameList - 处理人昵称列表
 */
const TooltipContent = ({ appId, pid, issueId, nicknameList }) => {
  const { t } = useTranslation();
  const convertTimeToAppTimeZone = useConvertTimeToAppTimeZone();

  // 状态文本映射，用于在tooltip中显示状态的中英文描述
  // 使用useMemo避免每次渲染都重新创建映射表
  const getStatusText = useMemo(() => {
    const statusMap = {
      [IssueStatus.NOT_PROCESSED]: t('statusOptions.未处理'),
      [IssueStatus.RESOLVED]: t('statusOptions.已处理'),
      [IssueStatus.RESOLVING]: t('statusOptions.处理中'),
      [IssueStatus.REOPENED]: t('statusOptions.重新打开'),
    };
    return (issueStatus) => statusMap[issueStatus] || ze('未知', 'Unknown');
  }, [t]);

  const [loading, setLoading] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null);
  // 使用sessionStorage缓存接口数据，key为issueId，value包含状态、时间和拉取时间
  // 好处：1. 减少接口调用 2. 实现不同组件实例之间的数据同步
  const [cacheMap, setCacheMap] = useSessionStorageState('issueStatusCache', {
    defaultValue: {},
  });

  useEffect(() => {
    if (!appId || !pid || !issueId) {
      return;
    }

    // 检查缓存是否存在且有效（一小时内）
    const cached = cacheMap[issueId];
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    if (cached && (now - cached.fetchTime) < oneHour) {
      // 缓存有效，直接使用缓存数据
      setStatusInfo(cached);
      return;
    }

    // 缓存无效或不存在，需要调用接口，显示loading
    setLoading(true);

    const fetchStatusHistory = async () => {
      try {
        // 调用接口获取问题的状态变更历史
        // 接口路径：/noteList/appId/{appId}/platformId/{pid}/issueId/{issueId}
        const rsp = await RestHelper.get(`/noteList/appId/${appId}/platformId/${pid}/issueId/${issueId}`);

        if (rsp && rsp.json && rsp.json.ret) {
          const noteList = rsp.json.ret;

          // 过滤掉issueStatus为USER_COMMENT(3)的记录，这些是用户备注，不是状态变更
          // 按createTime降序排序，取最近的一条状态变更记录
          const filteredList = noteList
            .filter(item => item.issueStatus !== IssueStatus.USER_COMMENT)
            .sort((a, b) => new Date(b.createTime) - new Date(a.createTime));

          if (filteredList.length > 0) {
            const latestNote = filteredList[0];
            const info = {
              issueStatus: latestNote.issueStatus,  // 最近一次的状态
              createTime: latestNote.createTime,    // 状态修改时间
              fetchTime: now,                       // 接口拉取时间，用于缓存过期判断
            };

            setStatusInfo(info);

            // 更新缓存到sessionStorage（使用函数式更新避免依赖cacheMap导致的无限循环）
            setCacheMap((prevCache) => ({
              ...prevCache,
              [issueId]: info,
            }));
          } else {
            setStatusInfo(null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch status history:', error);
        setStatusInfo(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStatusHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, pid, issueId]);

  // 渲染tooltip内容
  // 设置最小宽度和高度，防止加载中和加载完成后容器大小变化导致的视觉跳动
  return (
    <div style={{ minWidth: 180, minHeight: 50, maxWidth: 320 }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 60 }}>
          <Spin size="small" />
        </div>
      ) : (
        <>
          {/* 第一行：处理人列表 */}
          <div style={{ marginBottom: 4 }}>
            <span>{t('POPALERT.processor')}: {nicknameList.join(',')}</span>
          </div>
          {/* 第二行和第三行：状态和时间（仅在有数据时显示） */}
          {statusInfo && statusInfo.createTime && (
            <>
              <div style={{ marginBottom: 4 }}>
                <span>{t('REALTIMETOPISSUE.status')}: {getStatusText(statusInfo.issueStatus)}</span>
              </div>
              <div>
                <span style={{ whiteSpace: 'nowrap' }}>{ze('时间', 'Time')}: { convertTimeToAppTimeZone(statusInfo.createTime) }</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

/**
 * StatusItem - 问题处理状态展示组件
 * 用于平台多个页面的问题列表，展示问题状态、问题处理人
 *
 * 新增功能：
 * - 鼠标悬浮时，除了显示处理人列表，还显示问题最近一次状态修改的时间
 *
 * @param {Array} assigneeList - 处理人列表
 * @param {Function} handleChangeStatus - 状态变更处理函数
 * @param {number} statusNo - 状态编号（0:未处理, 1:已处理, 2:处理中, 4:重新打开）
 * @param {boolean} isOmitted - 是否省略显示处理人（只显示第一个+数量）
 * @param {string} appId - 应用ID（用于获取状态变更历史）
 * @param {string|number} pid - 平台ID/platformId（用于获取状态变更历史）
 * @param {string} issueId - 问题ID（用于获取状态变更历史）
 */
const StatusItem = (props) => {
  const {
    assigneeList = [],
    handleChangeStatus,
    statusNo = 0,
    isOmitted = false,
    appId,       // 新增：用于调用接口获取状态变更历史
    pid,         // 新增：即platformId，用于调用接口
    issueId,     // 新增：用于调用接口获取状态变更历史
  } = props;
  const { t } = useTranslation();
  const hasAssignee = assigneeList && assigneeList.length;
  const nicknameList = hasAssignee ? assigneeList.map(x => x.name) : [];
  const editStatusDom = <span className={_style.status_btn}>{ t('BASEPROBLEM.stateChange') }</span>;

  // 判断是否可以显示增强的tooltip（需要appId、pid、issueId三个参数）
  // 如果参数不全，则降级为原有的简单tooltip（只显示处理人）
  const canShowEnhancedTooltip = appId && pid && issueId;

  const statusDom = <Fragment>
    {STATUS_IMG[statusNo]}
    <Tooltip
      overlayClassName={_style.status_tooltip}
      placement="bottom"
      title={
        canShowEnhancedTooltip ? (
          // 增强版tooltip：显示处理人、状态、时间
          <TooltipContent
            appId={appId}
            pid={pid}
            issueId={issueId}
            nicknameList={nicknameList}
          />
        ) : (
          // 降级版tooltip：仅显示处理人
          <div style={{ maxWidth: 150 }}>{nicknameList.join(',')}</div>
        )
      }
    >
      {!isOmitted && <div className={_style.status_name_show}>{nicknameList.join(',')}</div>}
      {isOmitted && <div className={_style.status_name_show}>{nicknameList[0]}{nicknameList.length > 1 ? `,+${nicknameList.length - 1}` : ''}</div>}
    </Tooltip>
  </Fragment>;


  return <div className={_style.status_item} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5 }}
    onClick={() => {
      handleChangeStatus
        && handleChangeStatus();
    }}>
    {hasAssignee ? statusDom : editStatusDom}
  </div>;
};

StatusItem.propTypes = {
  handleChangeStatus: PropTypes.func.isRequired,
  assigneeList: PropTypes.array,
  statusNo: PropTypes.number,
  isOmitted: PropTypes.bool,
  // 以下三个参数为新增，用于获取状态变更历史
  appId: PropTypes.any,        // 应用ID
  pid: PropTypes.any,          // 平台ID（platformId）
  issueId: PropTypes.string,   // 问题ID
};

export default StatusItem;
